package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import com.advaita.cloud.drive.shared.FileKind
import com.advaita.cloud.drive.shared.GivenShareDto
import com.advaita.cloud.drive.ui.store.SharedByMeStore
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

private const val EMPTY_TITLE = "You have not shared anything yet"
private const val EMPTY_BODY = "Share files or folders from My Drive and they show up here."

/**
 * CR-6.3 (D10): Shared by me. One row for each live grant the caller made: the item, who got it,
 * the level, the date, and when it ends. Open shows the item. Stop sharing asks first.
 * A cap or an error takes the table's place, so an outage never reads as an empty list.
 */
@Composable
fun SharedByMeView(
    shared: SharedByMeStore,
    onOpen: (GivenShareDto) -> Unit,
    onStop: (GivenShareDto) -> Unit,
) {
    Div(attrs = { classes("shared-view", "shared-by-me-view"); attr("data-testid", "shared-by-me-view") }) {
        // An outage after a good read: the banner, over the rows of that read.
        shared.bannerSentence?.let { Div(attrs = { classes("shared-banner"); attr("role", "status") }) { Text(it) } }
        when {
            shared.loading && shared.count == 0 && shared.cap == null && shared.error == null ->
                Div(attrs = { classes("status-pad") }) { Text("Loading…") }
            shared.error != null -> Div(attrs = { classes("status-pad", "error"); attr("role", "alert") }) { Text(shared.error!!) }
            !shared.showTable && shared.cap != null -> CappedNotice(shared.cap!!)
            shared.isEmpty -> Div(attrs = { classes("status-pad", "shared-empty") }) {
                Div(attrs = { classes("shared-empty-title") }) { Text(EMPTY_TITLE) }
                Div { Text(EMPTY_BODY) }
            }
            else -> {
                Div(attrs = { classes("shared-toolbar") }) {
                    Input(InputType.Text, attrs = {
                        classes("modal-input", "shared-search")
                        attr("aria-label", "Search shared items")
                        placeholder("Search shared items")
                        value(shared.query)
                        onInput { shared.query = it.value }
                    })
                }
                Table(attrs = { classes("given-table"); attr("data-testid", "shared-by-me-table") }) {
                    Thead {
                        Tr {
                            Th { Text("Item") }
                            Th { Text("Shared with") }
                            Th { Text("Permission") }
                            Th { Text("Shared") }
                            Th { Text("Expires") }
                            Th(attrs = { classes("actions-col") }) { Text("") }
                        }
                    }
                    Tbody {
                        shared.visibleRows.forEach { row -> GivenRow(shared, row, onOpen, onStop) }
                    }
                }
                if (shared.noMatch) Div(attrs = { classes("status-pad") }) { Text("No items match") }
            }
        }
    }
}

@Composable
private fun GivenRow(
    shared: SharedByMeStore,
    row: GivenShareDto,
    onOpen: (GivenShareDto) -> Unit,
    onStop: (GivenShareDto) -> Unit,
) {
    val item = shared.itemLabel(row)
    val who = shared.recipientLabel(row)
    val expiry = shared.expiry(row)
    Tr(attrs = { attr("id", "given-${row.grantId}") }) {
        Td {
            Div(attrs = { classes("given-item") }) {
                Span { Text(item) }
                Div(attrs = { classes("sub") }) { Text(if (row.itemType == FileKind.FOLDER.name) "Folder" else "File") }
            }
        }
        Td(attrs = { classes("given-who") }) { Text(who) }
        Td(attrs = { classes("shared-level") }) { Text(shared.levelText(row)) }
        Td(attrs = { classes("sub") }) { Text(row.createdAt.take(10)) }
        Td(attrs = { classes("shared-expires"); if (expiry.soon) classes("expiry-soon") }) { Text(expiry.text) }
        Td(attrs = { classes("given-actions") }) {
            Button(attrs = {
                classes("link-btn")
                attr("aria-label", "Open $item")
                onClick { onOpen(row) }
            }) { Text("Open") }
            // An ended grant offers no Stop sharing: the vault already ended it.
            if (shared.stopOffered(row)) Button(attrs = {
                classes("btn")
                attr("aria-label", "Stop sharing $item with $who")
                if (!row.removable) {
                    attr("aria-disabled", "true")
                    attr("data-tip", "Only the owner or the person who shared this can change it.")
                }
                onClick { if (row.removable) onStop(row) }
            }) { Text("Stop sharing") }
        }
    }
}
